/**
 * Membership Models for Admin Portal
 *
 * Defines interfaces for member management in the admin portal.
 */

import { MembershipRoles } from './custom-claims.model';

// MembershipRoles is exported from custom-claims.model.ts (canonical source)
// Re-export here for backward compatibility
export { MembershipRoles } from './custom-claims.model';

/**
 * Basic member information shown in members list
 */
export interface AdminMemberView {
  membershipId: string;
  profileId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: number;
  combinedRole: number;
  isOwner: boolean;
  isChiefSupervisor: boolean;
  hasAcceptedInvite?: boolean;
  isActive: boolean;
  deletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  // Enriched fields
  certificationStatus?: 'valid' | 'expiring-soon' | 'expired' | 'missing';
  lastLogin?: string;
}

/**
 * Detailed member information shown in modal
 */
export interface MemberDetails extends AdminMemberView {
  phone?: string;
  dateOfBirth?: string;
  memberSince: string;
  lastLoginAt?: string;
  certifications?: MemberCertification[];
  activitySummary?: MemberActivity;
  organizations?: MemberOrganization[];
}

/**
 * Certification details for member
 */
export interface MemberCertification {
  id: string;
  certificationType: string;
  certificateNumber?: string;
  issuedDate?: string;
  expirationDate?: string;
  status: 'active' | 'expired' | 'expiring-soon' | 'pending-review';
  documentUrl?: string;
  documentFileName?: string;
}

/**
 * Activity summary for member
 */
export interface MemberActivity {
  flightLogsCount: number;
  sprayRecordsCount: number;
  lastActivityAt?: string;
}

/**
 * Organization membership for member
 */
export interface MemberOrganization {
  organizationId: string;
  organizationName: string;
  role: number;
  isOwner: boolean;
  isChiefSupervisor: boolean;
}

/**
 * Email status for invitations
 */
export type InvitationEmailStatus = 'never_sent' | 'sent' | 'failed';

/**
 * Pending invitation
 */
export interface PendingInvitation {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: number;
  invitedBy: string;
  inviterName?: string;
  inviterEmail?: string;
  expiresAt: string;
  createdAt: string;
  resendCount: number;
  lastResentAt?: string;
  emailStatus: InvitationEmailStatus;
  emailSentAt?: string;
  emailError?: string;
}

/**
 * Member list stats
 */
export interface MemberStats {
  total: number;
  active: number;
  inactive: number;
}

/**
 * Invitation stats
 */
export interface InvitationStats {
  total: number;
  pending: number;
  accepted: number;
  cancelled: number;
  expired: number;
}

/**
 * Helper functions for role management
 */
export class RoleHelper {
  static hasRole(combinedRole: number, role: MembershipRoles): boolean {
    return (combinedRole & role) === role;
  }

  static getRoleNames(combinedRole: number): string[] {
    const roles: string[] = [];
    if (this.hasRole(combinedRole, MembershipRoles.Owner)) {
      roles.push('Owner');
    }
    if (this.hasRole(combinedRole, MembershipRoles.ChiefSupervisor)) {
      roles.push('Chief Supervisor');
    }
    if (this.hasRole(combinedRole, MembershipRoles.PilotInCharge)) {
      roles.push('Pilot in Command');
    }
    if (this.hasRole(combinedRole, MembershipRoles.Pilot)) {
      roles.push('Pilot');
    }
    return roles;
  }

  static getRoleAbbreviation(combinedRole: number): string {
    const abbrevs: string[] = [];
    if (this.hasRole(combinedRole, MembershipRoles.Owner)) {
      abbrevs.push('Owner');
    }
    if (this.hasRole(combinedRole, MembershipRoles.ChiefSupervisor)) {
      abbrevs.push('CS');
    }
    if (this.hasRole(combinedRole, MembershipRoles.PilotInCharge)) {
      abbrevs.push('PIC');
    }
    if (this.hasRole(combinedRole, MembershipRoles.Pilot)) {
      abbrevs.push('Pilot');
    }
    return abbrevs.join(', ');
  }

  static getCertificationStatusClass(status: string): string {
    switch (status) {
      case 'active':
      case 'valid':
        return 'text-success';
      case 'expiring-soon':
        return 'text-warning';
      case 'expired':
      case 'missing':
        return 'text-danger';
      default:
        return 'text-secondary';
    }
  }

  static getCertificationStatusIcon(status: string): string {
    switch (status) {
      case 'active':
      case 'valid':
        return '🟢';
      case 'expiring-soon':
        return '🟡';
      case 'expired':
      case 'missing':
        return '❌';
      default:
        return '⚪';
    }
  }
}
